import React, { useRef, useState, useCallback } from 'react';
import DataGrid, {
  Column,
  Selection,
  Scrolling,
  SearchPanel,
  ColumnChooser,
  Export,
  Toolbar,
  Item,
  LoadPanel,
  FilterPanel,
  FilterBuilderPopup,
  DataGridRef,
  HeaderFilter,
  FilterRow,
  Sorting,
  MasterDetail,
  Grouping,
  GroupPanel
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import DropDownButton from 'devextreme-react/drop-down-button';
import DateBox from 'devextreme-react/date-box';
import { jsPDF as JsPdf } from 'jspdf';
import { saveAs } from 'file-saver-es';
import { Workbook } from 'exceljs';
import { exportDataGrid as exportDataGridToPdf } from 'devextreme/pdf_exporter';
import { exportDataGrid as exportDataGridToXLSX } from 'devextreme/excel_exporter';
import './CustomDataGrid.css';

interface ColumnConfig {
  dataField?: string;
  name?: string;
  caption: string;
  width?: number | string;
  dataType?: 'string' | 'number' | 'date' | 'datetime';
  format?: string;
  alignment?: 'left' | 'center' | 'right';
  cellRender?: (data: any) => React.ReactNode;
  allowFiltering?: boolean;
  allowSorting?: boolean;
  sortOrder?: 'asc' | 'desc';
  sortIndex?: number;
  fixed?: boolean;
  fixedPosition?: 'left' | 'right';
  groupIndex?: number;
  allowGrouping?: boolean;
}

interface CustomDataGridProps {
  // Zorunlu
  data: any[];
  columns: ColumnConfig[];
  keyExpr?: string;

  // Seçim
  selectedRowKeys?: any[];
  onSelectionChanged?: (keys: any[]) => void;
  selectionEnabled?: boolean;
  selectionMode?: 'single' | 'multiple' | 'none';
  selectAllMode?: 'allPages' | 'page';
  showCheckBoxesMode?: 'always' | 'onClick' | 'onLongTap' | 'none';
  allowSelectAll?: boolean;

  // Master-detail (satir genisleyince satir ici detay)
  masterDetailEnabled?: boolean;
  masterDetailComponent?: React.ComponentType<any>;
  masterDetailRender?: (e: any) => React.ReactNode;

  // Gruplama
  groupPanelVisible?: boolean;
  groupingAutoExpandAll?: boolean;

  // Arama
  searchPlaceholder?: string;

  // Tarih filtreleri (DateBox için)
  dateFilters?: Array<{
    value: Date | null;
    onValueChanged: (date: Date | null) => void;
    displayFormat?: string;
    width?: number;
  }>;

  // Butonlar
  onRefresh?: () => void;
  onAddNew?: () => void;
  onDelete?: (keys: any[]) => void;
  onExport?: (type: string, format: string) => void;
  onStatusFilter?: (status: string) => void;

  // Add button options
  addButtonOptions?: Array<{ id: number; text: string; icon?: string }>;
  onAddOptionClick?: (optionId: number) => void;

  // Export seçenekleri
  exportEnabled?: boolean;
  exportOptions?: Array<{ id: number; text: string; icon?: string }>;

  // Status filter
  statusFilterText?: string;
  statusFilterOptions?: Array<{ id: number; text: string }>;

  // Görünüm
  className?: string;
  height?: string;

  // Translations
  t?: (key: string) => string;

  // No data text
  noDataText?: string;

  // Row click handler
  onRowClick?: (data: any) => void;

  // Row context menu handler (right click)
  onRowContextMenu?: (e: React.MouseEvent, data: any) => void;

  // Content ready handler
  onContentReady?: (e: any) => void;

  // Additional toolbar items
  additionalToolbarItems?: Array<{
    location: 'before' | 'after' | 'center';
    render?: () => React.ReactNode;
    widget?: string;
    options?: any;
  }>;

  // Column chooser
  columnChooserEnabled?: boolean;

  // Filter row
  filterRowVisible?: boolean;

  // Allow column reordering
  allowColumnReordering?: boolean;

  // Row alternation
  rowAlternationEnabled?: boolean;

  // Focused row
  focusedRowKey?: any;
  autoFocus?: boolean;

  // Word wrap
  wordWrapEnabled?: boolean;

  // Focused row events
  onFocusedRowChanging?: (e: any) => void;
  onFocusedRowChanged?: (e: any) => void;
}

export const CustomDataGrid: React.FC<CustomDataGridProps> = ({
  data,
  columns,
  keyExpr = 'id',
  selectedRowKeys,
  onSelectionChanged,
  selectionEnabled = true,
  selectionMode = 'multiple',
  selectAllMode = 'allPages',
  showCheckBoxesMode = 'always',
  allowSelectAll,
  masterDetailEnabled = false,
  masterDetailComponent,
  masterDetailRender,
  groupPanelVisible = false,
  groupingAutoExpandAll = false,
  searchPlaceholder: _searchPlaceholder,
  dateFilters,
  onRefresh,
  onAddNew,
  onDelete,
  onExport,
  onStatusFilter,
  addButtonOptions,
  onAddOptionClick,
  exportEnabled = true,
  exportOptions,
  statusFilterText = 'Active',
  statusFilterOptions,
  className = 'custom-grid compact-grid',
  height = '100%',
  t = (key: string) => key,
  noDataText = 'Veri bulunamadı',
  onRowClick,
  onRowContextMenu,
  onContentReady,
  additionalToolbarItems,
  columnChooserEnabled = true,
  filterRowVisible = false,
  allowColumnReordering = true,
  rowAlternationEnabled = false,
  focusedRowKey,
  autoFocus = false,
  wordWrapEnabled: initialWordWrap = false,
  onFocusedRowChanging,
  onFocusedRowChanged,
}) => {
  const gridRef = useRef<DataGridRef>(null);
  const [showFilterRow, setShowFilterRow] = useState(filterRowVisible);
  const [wordWrapEnabled, setWordWrapEnabled] = useState(initialWordWrap);
  const [rowCount, setRowCount] = useState(0);
  const rowCountRef = useRef<HTMLSpanElement>(null);

  // Default: sağ tıkla satır değişimini engelle
  const handleFocusedRowChanging = useCallback((e: any) => {
    const nativeEvent = e?.event;
    const isRightClick = nativeEvent?.button === 2;
    const isContextMenuEvent = nativeEvent?.type === 'contextmenu' || nativeEvent?.type === 'dxcontextmenu';
    if (isRightClick || isContextMenuEvent) {
      e.cancel = true;
    }
    onFocusedRowChanging?.(e);
  }, [onFocusedRowChanging]);

  const updateRowCountDisplay = useCallback((count: number) => {
    if (rowCountRef.current) {
      rowCountRef.current.textContent = `${count} Kayıt`;
    }
  }, []);

  const didAutoFocus = useRef(false);
  const handleContentReady = useCallback((e: any) => {
    const count = e.component.totalCount();
    updateRowCountDisplay(count);
    if (rowCount === 0) {
      setRowCount(count);
    }
    // Sayfa acilir acilmaz klavye ok tuslari calissin diye grid'e DOM odagi ver
    if (autoFocus && !didAutoFocus.current && count > 0) {
      didAutoFocus.current = true;
      e.component.focus();
    }
    onContentReady?.(e);
  }, [rowCount, updateRowCountDisplay, onContentReady, autoFocus]);

  const handleExport = useCallback((type: 'all' | 'selected', format: 'excel' | 'pdf') => {
    const grid = gridRef.current?.instance();
    if (!grid) return;

    const fileName = `export_${new Date().toISOString().slice(0, 10)}`;

    if (format === 'pdf') {
      const doc = new JsPdf();
      exportDataGridToPdf({
        jsPDFDocument: doc,
        component: grid,
        selectedRowsOnly: type === 'selected',
      }).then(() => {
        doc.save(`${fileName}.pdf`);
      });
    } else {
      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet('Data');

      exportDataGridToXLSX({
        component: grid,
        worksheet,
        autoFilterEnabled: true,
        selectedRowsOnly: type === 'selected',
      }).then(() => {
        workbook.xlsx.writeBuffer().then((buffer) => {
          saveAs(new Blob([buffer], { type: 'application/octet-stream' }), `${fileName}.xlsx`);
        });
      });
    }
  }, []);

  const settingsOptions = [
    { id: 1, text: 'Reset Filters', icon: 'clear' },
    { id: 2, text: 'Reset Columns', icon: 'revert' },
    { id: 3, text: 'Export to Excel', icon: 'exportxlsx' },
    { id: 4, text: 'Export to PDF', icon: 'exportpdf' },
  ];

  const handleSettingsClick = (e: any) => {
    const grid = gridRef.current?.instance();
    if (!grid) return;

    switch (e.itemData.id) {
      case 1:
        grid.clearFilter();
        break;
      case 2:
        grid.state(null);
        break;
      case 3:
        handleExport('all', 'excel');
        break;
      case 4:
        handleExport('all', 'pdf');
        break;
    }
  };

  const defaultExportOptions = [
    { id: 1, text: 'Export all data to Excel', icon: 'exportxlsx' },
    { id: 2, text: 'Export selected rows to Excel', icon: 'exportxlsx' },
    { id: 3, text: 'Export all data to PDF', icon: 'exportpdf' },
    { id: 4, text: 'Export selected rows to PDF', icon: 'exportpdf' },
  ];

  return (
    <div className="custom-grid-wrapper"
      onContextMenu={onRowContextMenu ? (e: React.MouseEvent) => {
        const row = (e.target as HTMLElement).closest('.dx-data-row');
        if (!row) return;
        e.preventDefault();
        const gridInstance = gridRef.current?.instance();
        if (!gridInstance) return;
        const visibleRows = gridInstance.getVisibleRows();
        // Try aria-rowindex first, then fall back to DOM index
        const ariaIdx = parseInt(row.getAttribute('aria-rowindex') || '', 10);
        let matched = visibleRows.find((r: any) => r.rowIndex === ariaIdx - 1);
        if (!matched) {
          const rows = row.parentElement?.querySelectorAll('.dx-data-row');
          if (rows) {
            const domIdx = Array.from(rows).indexOf(row);
            if (domIdx >= 0 && domIdx < visibleRows.length) {
              matched = visibleRows[domIdx];
            }
          }
        }
        if (matched) {
          onRowContextMenu(e, matched.data);
        }
      } : undefined}
    >
    <DataGrid
      className={className}
      height={height}
      focusedRowEnabled
      focusedRowKey={focusedRowKey}
      keyExpr={keyExpr}
      dataSource={data}
      onRowClick={onRowClick ? (e) => onRowClick(e.data) : undefined}
      noDataText={noDataText}
      onContentReady={handleContentReady}
      onSelectionChanged={onSelectionChanged ? (e) => onSelectionChanged(e.selectedRowKeys) : undefined}
      allowColumnReordering={allowColumnReordering}
      rowAlternationEnabled={rowAlternationEnabled}
      allowColumnResizing
      columnResizingMode="widget"
      showBorders
      wordWrapEnabled={wordWrapEnabled}
      onFocusedRowChanging={handleFocusedRowChanging}
      onFocusedRowChanged={onFocusedRowChanged}
      ref={gridRef}
      onExporting={(e) => {
        // Prevent default export behavior - we handle it manually via toolbar buttons
        e.cancel = true;
      }}
    >
      <LoadPanel showPane={false} text={t("loading")} />
      <SearchPanel visible placeholder="Ara" width={150} />
      {columnChooserEnabled && <ColumnChooser title={t("columnChooser")} enabled mode="select" />}
      <FilterRow visible={showFilterRow} applyFilter="auto" />
      {exportEnabled && <Export enabled allowExportSelectedData formats={['xlsx', 'pdf']} />}
      {selectionEnabled && (
        <Selection
          mode={selectionMode}
          selectAllMode={selectAllMode}
          showCheckBoxesMode={showCheckBoxesMode}
          allowSelectAll={allowSelectAll}
        />
      )}
      {masterDetailEnabled && (masterDetailComponent || masterDetailRender) && (
        <MasterDetail
          enabled
          component={masterDetailComponent}
          render={masterDetailRender}
        />
      )}
      <FilterPanel visible={true} />
      <FilterBuilderPopup width="50%" height="50%" title={t("filterBuilder")} />
      <HeaderFilter visible />
      <Sorting mode="multiple" />
      {groupPanelVisible && <GroupPanel visible />}
      <Grouping autoExpandAll={groupingAutoExpandAll} />
      <Scrolling mode="virtual" />

      <Toolbar>
        {/* Search Panel */}
        <Item name="searchPanel" location="before" />

        {/* ABC - Filter Row Toggle */}
        <Item location="before">
          <Button
            hint="Filter Row"
            stylingMode="text"
            type={showFilterRow ? "default" : "normal"}
            onClick={() => setShowFilterRow(!showFilterRow)}
            render={() => (
              <span className="abc-filter-btn">
                A<span className="abc-filter-box">BC</span>
              </span>
            )}
          />
        </Item>

        {/* Column Chooser */}
        {columnChooserEnabled && <Item name="columnChooserButton" location="before" />}

        {/* Settings Dropdown */}
        <Item location="before">
          <DropDownButton
            icon="preferences"
            stylingMode="text"
            hint="Settings"
            displayExpr="text"
            keyExpr="id"
            items={settingsOptions}
            dropDownOptions={{ width: 200 }}
            onItemClick={handleSettingsClick}
            showArrowIcon={true}
          />
        </Item>

        {/* Export Dropdown */}
        <Item location="before">
          <DropDownButton
            icon="export"
            stylingMode="text"
            hint="Export"
            displayExpr="text"
            keyExpr="id"
            items={exportOptions || defaultExportOptions}
            dropDownOptions={{ width: 280 }}
            showArrowIcon={true}
            onItemClick={(e: any) => {
              const itemId = e.itemData.id;
              if (itemId === 1) handleExport('all', 'excel');
              else if (itemId === 2) handleExport('selected', 'excel');
              else if (itemId === 3) handleExport('all', 'pdf');
              else if (itemId === 4) handleExport('selected', 'pdf');
            }}
          />
        </Item>

        {/* Refresh Button */}
        {onRefresh && (
          <Item location="before">
            <Button
              icon="refresh"
              hint="Refresh"
              stylingMode="text"
              onClick={onRefresh}
            />
          </Item>
        )}

        {/* Separator */}
        {onRefresh && (statusFilterOptions || dateFilters) && (
          <Item location="before">
            <div className="h-6 w-px mx-2" style={{ backgroundColor: 'var(--border-color, #d1d5db)' }} />
          </Item>
        )}

        {/* Status Filter Dropdown */}
        {statusFilterOptions && statusFilterOptions.length > 0 && (
          <Item location="before">
            <DropDownButton
              items={statusFilterOptions.map(opt => opt.text)}
              stylingMode="text"
              text={statusFilterText}
              dropDownOptions={{ width: 'auto' }}
              useSelectMode
              showArrowIcon={true}
              onSelectionChanged={(e: any) => {
                if (e.item && onStatusFilter) {
                  onStatusFilter(e.item);
                }
              }}
            />
          </Item>
        )}

        {/* Date Filters */}
        {dateFilters && dateFilters.map((dateFilter, index) => (
          <Item key={`date-${index}`} location="before" locateInMenu="auto">
            <DateBox
              value={dateFilter.value || undefined}
              onValueChanged={(e) => dateFilter.onValueChanged(e.value || null)}
              displayFormat={dateFilter.displayFormat || 'dd.MM.yyyy'}
              type="date"
              width={dateFilter.width || 165}
              stylingMode="outlined"
              showClearButton={true}
            />
          </Item>
        ))}

        {/* Delete Selected Button */}
        {onDelete && selectedRowKeys && selectedRowKeys.length > 0 && (
          <Item location="before">
            <DropDownButton
              text={`(${selectedRowKeys.length}) ${t("transactions")}`}
              icon="menu"
              width={175}
              items={[
                { text: t("deleteSelected"), icon: 'trash', action: 'deleteSelected' },
              ]}
              onItemClick={(e: any) => {
                if (e.itemData.action === "deleteSelected") {
                  onDelete(selectedRowKeys);
                }
              }}
            />
          </Item>
        )}

        {/* Row Count */}
        <Item location="center" locateInMenu="auto">
          <span ref={rowCountRef} className="row-count-text">{rowCount} Kayıt</span>
        </Item>

        {/* Add New Button */}
        {onAddNew && (
          <Item location="after">
            {addButtonOptions && addButtonOptions.length > 0 ? (
              <DropDownButton
                icon="plus"
                type="default"
                stylingMode="contained"
                displayExpr="text"
                keyExpr="id"
                items={addButtonOptions}
                dropDownOptions={{ width: 200 }}
                showArrowIcon={true}
                splitButton={false}
                onButtonClick={onAddNew}
                onItemClick={(e: any) => {
                  if (onAddOptionClick) {
                    onAddOptionClick(e.itemData.id);
                  }
                }}
              />
            ) : (
              <Button
                icon="plus"
                type="default"
                stylingMode="contained"
                onClick={onAddNew}
              />
            )}
          </Item>
        )}

        {/* Additional Toolbar Items (collapse buttons etc.) */}
        {additionalToolbarItems && additionalToolbarItems.map((item, index) => (
          <Item key={`additional-${index}`} location={item.location} locateInMenu="auto">
            {item.render ? item.render() : null}
          </Item>
        ))}
      </Toolbar>

      {columns.map((col: ColumnConfig, index: number) => (
        <Column
          key={index}
          dataField={col.dataField}
          name={col.name || col.dataField || `col_${index}`}
          caption={col.caption}
          width={col.width}
          dataType={col.dataType}
          format={col.format}
          alignment={col.alignment}
          cellRender={col.cellRender}
          allowFiltering={col.allowFiltering !== false && !!col.dataField}
          allowSorting={col.allowSorting !== false && !!col.dataField}
          sortOrder={col.sortOrder}
          sortIndex={col.sortIndex}
          fixed={col.fixed}
          fixedPosition={col.fixedPosition}
          groupIndex={col.groupIndex}
          allowGrouping={col.allowGrouping}
        />
      ))}
    </DataGrid>
    </div>
  );
};

export default CustomDataGrid;
